import React, { useState } from "react";
import {
  AlertTriangle,
  BookOpenText,
  Mail,
  Phone,
  Send,
  UserRound,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { createPublicComplaint } from "../services/publicService";
import "./FraudAlertTicketPage.css";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SUBJECT_OPTIONS = [
  "Suspicious Offer Letter",
  "Recruitment Fraud",
  "Fake Job Communication",
  "Identity Misuse",
  "Other",
];

const FraudAlertTicketPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState(() => ({
    candidateName: location.state?.candidateName || "",
    candidateEmail: location.state?.candidateEmail || "",
    candidateMobileNumber:
      location.state?.candidateMobileNumber || "",
    subject: SUBJECT_OPTIONS[0],
    specifySubject: "",
    description: "",
  }));

  const [fieldErrors, setFieldErrors] = useState({});
  const [submitState, setSubmitState] = useState({
    isSubmitting: false,
    popup: null,
  });

  const validateField = (name, value) => {
      const v = value.trim();
  
      if (name === "candidateName") {
        return v ? "" : "Please enter Candidate Name.";
      }
  
      if (name === "candidateEmail") {
      if (!v) return "Please enter Candidate Email.";
      if (!EMAIL_PATTERN.test(v))
        return "Please enter a valid Candidate Email.";
    }

    if (name === "candidateMobileNumber") {
      return v ? "" : "Please enter Mobile Number.";
    }

    if (name === "subject") {
      return v ? "" : "Please select a subject.";
    }

    if (name === "specifySubject" && formData.subject === "Other") {
      return v ? "" : "Please enter Specify Subject.";
    }

    if (name === "description") {
      return v ? "" : "Please enter Description.";
    }

    return "";
  };

  const getFormErrors = (values) => {
    const errors = {};
    Object.entries(values).forEach(([k, v]) => {
      const err = validateField(k, v);
      if (err) errors[k] = err;
    });
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      if (name === "subject" && value !== "Other") {
        return { ...prev, subject: value, specifySubject: "" };
      }
      return { ...prev, [name]: value };
    });

    setFieldErrors((prev) => {
      const next = { ...prev };
      const err = validateField(name, value);

      if (err) next[name] = err;
      else delete next[name];

      if (name === "subject" && value !== "Other") {
        delete next.specifySubject;
      }

      return next;
    });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const err = validateField(name, value);

    setFieldErrors((prev) => {
      const next = { ...prev };
      if (err) next[name] = err;
      else delete next[name];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = getFormErrors(formData);
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) return;

    setSubmitState({ isSubmitting: true, popup: null });

    try {
      const res = await createPublicComplaint({
        name: formData.candidateName.trim(),
        email: formData.candidateEmail.trim(),
        phone: formData.candidateMobileNumber.trim(),
        subject:
          formData.subject === "Other"
            ? formData.specifySubject.trim()
            : formData.subject.trim(),
        description: `Mobile Number: ${formData.candidateMobileNumber.trim()}\n\n${formData.description.trim()}`,
      });

      setSubmitState({
        isSubmitting: false,
        popup: {
          variant: "success",
          title: "Ticket Submitted",
          message:
            res?.message || "Complaint submitted successfully.",
        },
      });
    } catch (err) {
      setSubmitState({
        isSubmitting: false,
        popup: {
          variant: "error",
          title: "Submission Failed",
          message:
            err?.response?.data?.message ||
            "Failed to submit complaint.",
        },
      });
    }
  };

  const handleClosePopup = () => {
    if (submitState.popup?.variant === "success") {
      navigate("/");
      return;
    }

    setSubmitState((prev) => ({ ...prev, popup: null }));
  };

  return (
    <div className="fraud-ticket-page">
      <main className="fraud-ticket-main">
        <div className="fraud-ticket-icon-shell">
          <AlertTriangle className="fraud-ticket-hero-icon" />
        </div>

        <h1 className="fraud-ticket-title">
          Raise the Ticket for Fraud Alert
        </h1>

        <p className="fraud-ticket-subtitle">
          Submit candidate details to raise a fraud alert ticket.
        </p>

        <form className="fraud-ticket-card" onSubmit={handleSubmit}>
          <h2 className="fraud-ticket-card-title">
            Candidate Information
          </h2>

          <div className="fraud-ticket-grid">
            {/* NAME */}
            <label
              className={`fraud-ticket-field${
                fieldErrors.candidateName ? " has-error" : ""
              }`}
            >
              <span className="fraud-ticket-label">
                <UserRound size={16} /> Candidate Name
              </span>
              <input
                name="candidateName"
                value={formData.candidateName}
                onChange={handleChange}
                onBlur={handleBlur}
                className={
                  fieldErrors.candidateName ? "is-error" : ""
                }
              />
              {fieldErrors.candidateName && (
                <span className="fraud-ticket-error">
                  {fieldErrors.candidateName}
                </span>
              )}
            </label>

            <label
              className={`fraud-ticket-field${
                fieldErrors.candidateEmail ? " has-error" : ""
              }`}
            >
              <span className="fraud-ticket-label">
                <Mail size={16} /> Candidate Email
              </span>
              <input
                name="candidateEmail"
                value={formData.candidateEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                className={
                  fieldErrors.candidateEmail ? "is-error" : ""
                }
              />
              {fieldErrors.candidateEmail && (
                <span className="fraud-ticket-error">
                  {fieldErrors.candidateEmail}
                </span>
              )}
            </label>

            {/* PHONE */}
            <label
              className={`fraud-ticket-field${
                fieldErrors.candidateMobileNumber
                  ? " has-error"
                  : ""
              }`}
            >
              <span className="fraud-ticket-label">
                <Phone size={16} /> Mobile Number
              </span>
              <input
                name="candidateMobileNumber"
                value={formData.candidateMobileNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                className={
                  fieldErrors.candidateMobileNumber
                    ? "is-error"
                    : ""
                }
              />
              {fieldErrors.candidateMobileNumber && (
                <span className="fraud-ticket-error">
                  {fieldErrors.candidateMobileNumber}
                </span>
              )}
            </label>

            {/* SUBJECT */}
            <label className="fraud-ticket-field fraud-ticket-field-full">
              <span className="fraud-ticket-label">
                <BookOpenText size={16} /> Subject
              </span>
              <select
                name="subject"
                value={formData.subject}
                onChange={handleChange}
              >
                {SUBJECT_OPTIONS.map((opt) => (
                  <option key={opt}>{opt}</option>
                ))}
              </select>
            </label>

            {/* DESCRIPTION */}
            <label
              className={`fraud-ticket-field fraud-ticket-field-full${
                fieldErrors.description ? " has-error" : ""
              }`}
            >
              <span className="fraud-ticket-label">
                <BookOpenText size={16} /> Description
              </span>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                onBlur={handleBlur}
                className={
                  fieldErrors.description ? "is-error" : ""
                }
              />
              {fieldErrors.description && (
                <span className="fraud-ticket-error">
                  {fieldErrors.description}
                </span>
              )}
            </label>
          </div>

          <button
            type="submit"
            className="fraud-ticket-submit"
            disabled={submitState.isSubmitting}
          >
            <Send size={16} />
            {submitState.isSubmitting
              ? "Submitting..."
              : "Submit Fraud Alert Ticket"}
          </button>
        </form>
      </main>

      {/* POPUP */}
      {submitState.popup && (
        <div
          className="fraud-ticket-popup-overlay"
          onClick={handleClosePopup}
        >
          <div
            className="fraud-ticket-popup-card"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className={`fraud-ticket-popup-title ${
                submitState.popup.variant === "success"
                  ? "is-success"
                  : "is-error"
              }`}
            >
              {submitState.popup.title}
            </h3>

            <p className="fraud-ticket-popup-message">
              {submitState.popup.message}
            </p>

            <div className="fraud-ticket-popup-actions">
              <button
                className="fraud-ticket-popup-btn"
                onClick={handleClosePopup}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FraudAlertTicketPage;
